import { useEffect, useMemo, useState } from 'react';
import Button from 'components/commercetools-ui/atoms/button';
import Image, { ImageProps } from 'components/commercetools-ui/atoms/image';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import useWindowWidth from 'scripts/hooks';
import { Reference } from 'types/reference';

export interface Hero2Props {
  image: ImageProps;
  title: string;
  subtitle: string;
  ctaLabel?: string;
  ctaReference?: Reference;
  textPotion?: number;
  backgroundColor?: string;
  bannerStyle?: 'side-by-side' | 'text-overlay';
  textAlign?: 'text-start' | 'text-center' | 'text-end';
  textJustify?: 'justify-start' | 'justify-center' | 'justify-end';
}

const Hero2: React.FC<Hero2Props> = ({
  image,
  title,
  subtitle,
  ctaLabel,
  ctaReference,
  textPotion = 50,
  backgroundColor,
  bannerStyle = 'text-overlay',
  textAlign = 'text-center',
  textJustify = 'justify-center',
}) => {
  const windowWidth = useWindowWidth();
  const [mobileView, setMobileView] = useState(true);
  const cropFocus = useMemo(() => {
    if (image.gravity?.coordinates) {
      const right = Math.ceil((image.gravity?.coordinates.x / (image.media?.width as number)) * 100);
      const top = Math.ceil((image.gravity?.coordinates.y / (image.media?.height as number)) * 100);

      return `right ${right.toString()}% top ${top.toString()}%`;
    }

    if (image.gravity?.mode === 'center') {
      return 'center';
    }

    return 'initial';
  }, [image]);

  useEffect(() => {
    setMobileView(windowWidth !== null && windowWidth < 768);
  }, [windowWidth]);
  return (
    <>
      {bannerStyle == 'text-overlay' ? (
        <div className="relative mt-0 w-full md:mt-60 xl:mt-0">
          {image && (
            <div className="relative h-[50vh]">
              <Image
                {...image}
                priority
                loading="eager"
                alt={title}
                fill
                title={''}
                style={{ objectFit: 'cover', objectPosition: cropFocus }}
              />
            </div>
          )}
          <div
            className={`${textAlign} ${textJustify} absolute left-0 top-0 flex size-full flex-col px-48 py-6 md:px-48 md:py-12 lg:px-48 lg:py-24`}
            style={{
              ...(!mobileView && { width: `calc(100% * ${textPotion || 100} / 100)` }),
            }}
          >
            <Typography as="h1" className="mt-22 text-26 text-white md:mt-32 md:text-36 lg:text-58">
              {title}
            </Typography>
            <Typography className="text-14 text-white md:text-18">{subtitle}</Typography>
            {ctaLabel && (
              <Link link={ctaReference}>
                <Button className="mt-22 md:mt-36 md:px-48 md:py-12 lg:mt-32">
                  <Typography as="span" className="text-12 text-neutral-150 md:text-14 lg:text-16">
                    {ctaLabel}
                  </Typography>
                </Button>
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-col md:flex-row">
          <div
            className={`flex w-full flex-col items-start px-48 py-24 md:py-0`}
            style={{
              backgroundColor: backgroundColor,
              width: mobileView ? '100%' : `${textPotion || 50}%`,
            }}
          >
            <div className={`flex size-full flex-col ${textJustify} ${textAlign}`}>
              <Typography className={`text-14 text-black md:text-18`}>{subtitle}</Typography>
              <Typography as="h1" className={`mx-auto my-22 text-26 text-black md:m-0 md:mt-32 md:text-36 lg:text-58`}>
                {title}
              </Typography>
              {ctaLabel && (
                <Link link={ctaReference}>
                  <Button className="mt-22 md:mt-36 md:px-48 md:py-12 lg:mt-32">
                    <Typography as="span" className="text-12 text-neutral-150 md:text-14 lg:text-16">
                      {ctaLabel}
                    </Typography>
                  </Button>
                </Link>
              )}
            </div>
          </div>
          {image && (
            <div
              className={`h-296 w-full md:h-[532px] lg:h-[668px]`}
              style={{ width: mobileView ? '100%' : `${100 - (textPotion || 50)}%` }}
            >
              <Image
                {...image}
                priority
                loading="eager"
                alt={title}
                title={''}
                style={{ objectFit: 'cover', objectPosition: cropFocus }}
                className="size-full brightness-75"
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Hero2;
